import React, { useRef, useEffect, useState } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';
import logger from 'ws-scripts/modules/logger';
import { useLabels, usePrefs } from 'wsm-common-data';
import { ErrorBoundary } from 'wsm-error-boundary';
import Skeleton from 'wsm-skeleton';
import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import { useWindowDimension } from 'wsm-srp-utilities';
import {
	getPaymentsEnabled,
	getPlaceholderLayout,
	getUserShowPayments,
	setHideTooltip
} from '../utilities/wiapiSettings';

import {
	formatAppliedFilters,
	getFeaturedPromotionFilter
} from '../utilities/filters';

import HighlightFacet from '../components/HighlightFacet';
import FiltersContainer from '../components/FiltersContainer';
import { useScrollEffect } from '../hooks/useScrollEffect';
import SpellcheckMsg from '../components/SpellcheckMsg';

import {
	SHOW_PAYMENTS_LOCAL_STORAGE_KEY,
	SHOW_PAYMENTS_NOTIFICATION,
	WIDGET_NAME
} from '../global-constants';

import '../global-sass/style.scss';
import '../global-sass/switch.scss';

const waitForInvData = (registerType = 'inventory') => {
	let waitForInvDataHandle;
	const checkForInvData = () => {
		if (window?.DDC?.InvData) {
			if (registerType === 'facet') {
				window.DDC.InvData.registerFacetNotification();
			} else if (registerType === 'inventory') {
				window.DDC.InvData.registerInventoryNotification();
			} else if (registerType === 'sortBy') {
				window.DDC.InvData.registerSortByNotification();
			}
			clearInterval(waitForInvDataHandle);
		}
	};
	waitForInvDataHandle = setInterval(checkForInvData, 50);
};

const waitForWIAPISettings = () => {
	return new Promise((resolve) => {
		let waitForSettings;
		const checkForSettings = () => {
			const placeholderLayout = getPlaceholderLayout();
			const paymentsEnabled = getPaymentsEnabled();
			const showPaymentsToggleEnabled = getUserShowPayments();

			if (
				typeof placeholderLayout !== 'undefined' &&
				typeof paymentsEnabled !== 'undefined' &&
				typeof showPaymentsToggleEnabled !== 'undefined'
			) {
				clearInterval(waitForSettings);
				resolve(true);
			}
		};
		waitForSettings = setInterval(checkForSettings, 50);
	});
};

const Widget = () => {
	const labels = useLabels();
	const { enableHLFacet, chipColorClasses, showCountOnPill } = usePrefs();
	const stickyFiltersRef = useRef(null);
	const [invCount, setInvCount] = useState(null);
	const [appliedFilters, setAppliedFilters] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [facets, setFacets] = useState(null);
	const [useOriginalSearchTerm, setUseOriginalSearchTerm] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [showPaymentsChecked, setShowPaymentsChecked] = useState(false);

	// Update the state of the Show Payments toggle.
	const handleShowPaymentsUpdate = (override, forcePopup = false) => {
		const useOverride = typeof override !== 'object';

		setShowPaymentsChecked((prevState) => {
			// Use the value passed in, if present.
			// Otherwise just toggle the state of the switch.
			const newState = useOverride ? override : !prevState;
			if (isBrowser) {
				// Save the state in local storage.
				window.localStorage.setItem(
					SHOW_PAYMENTS_LOCAL_STORAGE_KEY,
					JSON.stringify({ show: newState })
				);

				// Publish the current state of the toggle as an event for other
				// widgets to listen for when deciding how to render their content.
				if (window?.DDC?.pubsub) {
					window.DDC.pubsub.publish(SHOW_PAYMENTS_NOTIFICATION, {
						show: newState,
						source: WIDGET_NAME,
						isOverride: useOverride,
						forcePopup
					});
				}
			}
			return newState;
		});
	};

	const isStickyDisabled =
		(isBrowser &&
			window?.DDC?.siteProperties?.templateHeaderDisableSticky) ||
		false;

	let navbarHeightCalc = 0;

	const dimensions = useWindowDimension();
	const isDesktop = dimensions?.viewportWidth > 992;

	const navbarHeight =
		!isDesktop && !isStickyDisabled
			? isBrowser && document.querySelector('.page-header').offsetHeight
			: 0;

	const navbarHeightShrink = !isDesktop
		? isBrowser &&
		  document.querySelector('.navbar-default.sticky-header-nav')
		: null;

	const navbarHeightFixedMobile = !isDesktop
		? isBrowser &&
		  document.querySelector(
				'div[data-widget-id="template-navbar1"] .navbar.fixed'
		  )
		: null;

	if (navbarHeightShrink !== null) {
		navbarHeightCalc = navbarHeight + navbarHeightShrink.offsetHeight;
	} else if (navbarHeightFixedMobile !== null && navbarHeight === 0) {
		navbarHeightCalc = navbarHeightFixedMobile.offsetHeight;
	} else {
		navbarHeightCalc = navbarHeight;
	}

	// Ensure the tooltip will remain hidden if the user is already viewing payments.
	if (getUserShowPayments()) {
		setHideTooltip();
	}

	useEffect(() => {
		if (!window?.DDC?.pubsub) {
			return;
		}

		(async () => {
			await waitForInvData('facet');
			await waitForInvData('sortBy');

			const getFacets = () => {
				const facetData = window.DDC.InvData.getFacets();
				const filterData = window.DDC.InvData.getAppliedFilters();
				const featuredPromotion =
					window.DDC.InvData?.urlParams?.featuredPromotion;
				if (featuredPromotion) {
					filterData.unshift(getFeaturedPromotionFilter(labels));
				}

				setFacets(facetData.facets);
				setTotalCount(facetData.totalCount);
				setAppliedFilters(formatAppliedFilters(filterData, labels));

				waitForWIAPISettings().then(() => {
					// Set the toggle status that is defined in local storage.
					const showPaymentsToggleEnabled = getUserShowPayments();

					if (typeof showPaymentsToggleEnabled !== 'undefined') {
						handleShowPaymentsUpdate(showPaymentsToggleEnabled);
					}

					// Allow external events to change the state of the Show Payments toggle.
					window.DDC.pubsub.subscribe(
						SHOW_PAYMENTS_NOTIFICATION,
						(data) => {
							if (data.source !== WIDGET_NAME) {
								handleShowPaymentsUpdate(data.show);
							}
						}
					);

					setIsLoading(false);

					// Send an event telling ws-inv-listing that it is safe to render the View Toggle portal.
					window.DDC.pubsub.publish(
						'ws-inv-filters/toggle-portal-ready',
						true
					);
				});
			};

			const getInvCount = () => {
				const { pageInfo } = window?.DDC?.InvData?.getInventory() || {};
				setInvCount(pageInfo?.totalCount || null);
			};

			window.DDC.pubsub.subscribe('ws-inv-data/facets', () => {
				getFacets();
			});
			window.DDC.pubsub.subscribe('ws-inv-data/inventory', () => {
				getInvCount();
			});

			window.DDC.pubsub.subscribe('ws-inv-data/sortBy', () => {
				const filterData = window.DDC.InvData.getAppliedFilters();
				setAppliedFilters(formatAppliedFilters(filterData, labels));
			});

			window.DDC.pubsub.subscribe('ws-inv-data/spellcheck', () => {
				if (!window.localStorage.getItem('forceOriginalSearch')) {
					setUseOriginalSearchTerm(true);
				}
			});

			window.DDC.pubsub.subscribe(
				'ws-inv-data/clearSpellcheckMsg',
				() => {
					setUseOriginalSearchTerm(useOriginalSearchTerm);
				}
			);
			window.DDC.pubsub.subscribe('ws-inv-data/request-failed', () => {
				setIsLoading(false);
			});

			// There is a chance that the topic 'ws-inv-data/inventory' or 'ws-inv-data/facets' is published by inv-data
			// before the inv-filter subscribes to it. This results in the filter being stuck
			// in the skeleton loader phase.
			if (isLoading && window.DDC && window.DDC.InvData) {
				if (Object.keys(window.DDC.InvData.facets).length) {
					getFacets();
				}
				if (Object.keys(window.DDC.InvData.inventory).length) {
					getInvCount();
				}
			}
		})();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps
	// labels do not update and should not be a dependency of this useEffect
	// including it was causing unnecessary rerenders
	// in most cases, common data should not be included in dependency arrays

	const isScrolled = useScrollEffect(stickyFiltersRef, navbarHeightCalc);

	if (!(isBrowser && window.DDC.InvData && facets)) {
		if (isLoading) {
			return (
				<>
					{enableHLFacet && (
						<div>
							<Skeleton
								skeletonClasses="mb-4"
								aspectRatio="4by3"
								height="38px"
							/>
						</div>
					)}
					<div className="row gap-skeleton">
						<div className="col-xs-6 col-md-8">
							<Skeleton aspectRatio="4by3" height="38px" />
						</div>
						<div className="col-xs-6 col-md-4">
							<Skeleton aspectRatio="4by3" height="38px" />
						</div>
					</div>
				</>
			);
		}
	}
	// Currently only supported for multi select facet
	const highLightFacet = facets.find(
		(item) =>
			item.id === enableHLFacet &&
			item.multiselect &&
			item.type === 'VALUE'
	);

	return (
		<ErrorBoundary
			errorHandler={(error, errorInfo) => {
				setNewRelicCustomAttribute(
					'SRP ERROR',
					`ws-inv-filters error boundary.\n${error}`
				);
				const newError = new Error(
					`ws-inv-filters error boundary.\n${error}`
				);
				newError.originalError = error;
				newError.originalStackTrace = errorInfo.componentStack;
				logger.error(`${newError}\n${newError.originalStackTrace}`);
			}}
		>
			{highLightFacet && (
				<div className="facet-hl-container mt-n4 d-flex align-items-center">
					<HighlightFacet
						highLightFacet={highLightFacet}
						chipColorClasses={chipColorClasses}
						showCountOnPill={showCountOnPill}
					/>
				</div>
			)}
			<div className="facet-filters-ref" ref={stickyFiltersRef}>
				<FiltersContainer
					appliedFilters={appliedFilters}
					invCount={invCount}
					isDesktop={isDesktop}
					isScrolled={isScrolled}
					navbarHeightCalc={navbarHeightCalc}
					totalCount={totalCount}
					showPaymentsChecked={showPaymentsChecked}
					handleShowPaymentsUpdate={handleShowPaymentsUpdate}
				/>
				{useOriginalSearchTerm && <SpellcheckMsg />}
			</div>
		</ErrorBoundary>
	);
};

export default Widget;
